import type {
  GlobalFilters,
  ProcessCatalogView,
  ProcessDetailView,
  QueryResult,
} from '@aurora-pvi/contracts';
import { apiFetch } from './apiFetch';

/**
 * The process catalogue and one process's record, from Postgres.
 *
 * `core.process` held 71 rows for the life of the schema, with 2,694
 * `IMPLEMENTS_PROCESS` and 534 `INCLUDES_PROCESS` edges hanging off them, and
 * these two screens described a catalogue that existed nowhere else.
 *
 * The plain interfaces these return carry no zod schema, so there is nothing to
 * parse against here — the contract file says why, and says a schema belongs
 * with the endpoint that has to validate a payload. That endpoint now exists,
 * which makes writing one the next honest step rather than this one.
 */
export function createHttpProcessRepository(baseUrl: string) {
  return {
    async getCatalog(_filters: GlobalFilters): Promise<QueryResult<ProcessCatalogView>> {
      const response = await apiFetch(`${baseUrl}/processes`);
      if (!response.ok) throw new Error(`GET /processes failed: ${response.status}`);
      return (await response.json()) as QueryResult<ProcessCatalogView>;
    },

    /**
     * Null data is the answer for an id the catalogue does not hold, and the
     * screen renders that rather than a blank — a 404 here is "no such process",
     * not "the request failed".
     */
    async getDetail(
      processId: string | undefined,
      _filters: GlobalFilters,
    ): Promise<QueryResult<ProcessDetailView | null>> {
      if (!processId) {
        throw new Error('A process must be named before its record can be read.');
      }
      const response = await apiFetch(`${baseUrl}/processes/${encodeURIComponent(processId)}`);
      if (response.status === 404) {
        const body = (await response.json().catch(() => ({}))) as { meta?: unknown };
        return { data: null, meta: (body.meta ?? null) as never };
      }
      if (!response.ok) throw new Error(`GET /processes/:id failed: ${response.status}`);
      return (await response.json()) as QueryResult<ProcessDetailView | null>;
    },
  };
}
